import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio, FormControl, Select,
  MenuItem, InputLabel, ListItemText, Checkbox, Grid
} from "@mui/material";
import { useParams } from 'react-router-dom';
import { Helmet } from "react-helmet";
import Toast from '../components/Toast';
import HelpTooltip from '../components/HelpTooltip';

export default function EditSong() {
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("1");
  const [song, setSong] = useState("");
  const [songnumber, setSongNumber] = useState("");
  const [author, SetAuthor] = useState("");
  const [engtitle, setEngTitle] = useState("");
  const [engLyrics, setEngLyrics] = useState("");
  const [copyright, setCopyright] = useState("no");
  const [customSection, setCustomSection] = useState("Glocal Prayer Network");
  const [createdSections, setCreatedSections] = useState(() => {
    try {
      const stored = localStorage.getItem("gpn_songs_custom_sections");
      const parsed = stored ? JSON.parse(stored) : [];
      const names = parsed.map(p => p.name).filter(Boolean);
      return Array.from(new Set(["Glocal Prayer Network", ...names]));
    } catch {
      return ["Glocal Prayer Network"];
    }
  });
  const API_URL = process.env.REACT_APP_API_URL;

  const { id } = useParams();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    // Replace this with your actual API call
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}get-song-details`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ id: id })
    });
    const data = await response.json();

      if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
    }

    setTitle(data.data.title);
    setLanguage(data.data.language_id);
    setSong(data.data.song);
    setSongNumber(data.data.song_id);
    SetAuthor(data.data.author);
    setEngTitle(data.data.eng_title);
    setEngLyrics(data.data.eng_lyrics);
    setCopyright(data.data.copyright);
    if (data.data.custom_section) {
      setCustomSection(data.data.custom_section);
      setCreatedSections(prev => Array.from(new Set([...prev, data.data.custom_section])));
    }
  };




  const handleAddSong = async () => {
    const token = localStorage.getItem('token');

    if (title == '' || song == '' || songnumber == '') {
      setToast({ open: true, message: "Title and Song are required fields", severity: "error" });
      return false;
    }
    const response = await fetch(`${API_URL}update-song`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        // ❌ Do NOT set 'Content-Type' when sending FormData
      },
      body: JSON.stringify({
        title: title,
        language: language,
        song: song,
        songnumber: songnumber,
        author: author,

        eng_title: engtitle,
        eng_lyrics: engLyrics,
        copyright: copyright,
        custom_section: customSection,
        id: id
      })
    })

    const data = await response.json();

    if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
    }

    if (response.ok) {
      setToast({ open: true, message: "Song updated successfully", severity: "success" });
      setTimeout(() => window.location.reload(), 1000);
    } else {
      setToast({ open: true, message: "Update failed: " + (data?.message || 'Unknown error'), severity: "error" });
    }
  };

  return (
    <>
      <Helmet>
        <title>Edit Song - GPN</title>
      </Helmet>
      <Box display="flex" justifyContent="center" mt={4}>
        <Paper elevation={3} sx={{ p: 4, width: "100%", maxWidth: "900px" }}>
          <Typography variant="h5" gutterBottom>
            Edit Song
          </Typography>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            justifyContent="space-between"
          >
            <Box flex="1 1 31%">
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>Song Collection / Section *</Typography>
                <HelpTooltip
                  title="Song Collection"
                  purpose="Organizes this song into a specific collection (e.g. Glocal Prayer Network, Daily Devotions)."
                  howItWorks="Choose an existing collection or assign to Glocal Prayer Network."
                  mobileEffect="Users can browse songs grouped by their respective collection."
                />
              </Box>
              <FormControl fullWidth size="small">
                <Select value={customSection} onChange={(e) => setCustomSection(e.target.value)}>
                  {createdSections.map(sec => (
                    <MenuItem key={sec} value={sec}>{sec}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box flex="1 1 31%">
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>Language *</Typography>
                <HelpTooltip
                  title="Song Language"
                  purpose="Specifies the primary language in which the song was written and is sung."
                  howItWorks="Select from English, Telugu, Hindi, or other available languages."
                  mobileEffect="Users in the mobile app can filter and browse songs by this selected language."
                />
              </Box>
              <FormControl fullWidth required size="small">
                <Select value={language} onChange={(e) => setLanguage(e.target.value)}>
                  <MenuItem value="1">English</MenuItem>
                  <MenuItem value="2">Telugu</MenuItem>
                  <MenuItem value="3">Hindi</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Song Number */}
            <Box flex="1 1 31%">
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>Song Number *</Typography>
                <HelpTooltip
                  title="Song Index Number"
                  purpose="Unique sequence identifier for the hymn in the hymnal collection."
                  howItWorks="Automatically allocated based on the highest existing hymn number."
                  mobileEffect="Allows mobile users to quickly search and jump to this song by entering its number."
                />
              </Box>
              <TextField size="small" disabled value={songnumber} required fullWidth onChange={(e) => setSongNumber(e.target.value)} />
            </Box>

            {/* Title */}
            <Box flex="1 1 100%" mt={1}>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>Song Title *</Typography>
                <HelpTooltip
                  title="Song Title"
                  purpose="Primary name or opening line of the hymn / worship song."
                  howItWorks="Enter the exact title in the native language."
                  mobileEffect="Displayed as the primary headline in the mobile app songbook and search results."
                />
              </Box>
              <TextField size="small" value={title} required fullWidth onChange={(e) => setTitle(e.target.value)} />
            </Box>
          </Box>

          {/* Description */}
          <Box mt={2}>
            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
              <Typography variant="subtitle2" fontWeight={600}>Song Lyrics *</Typography>
              <HelpTooltip
                title="Song Lyrics"
                purpose="Complete verses and chorus of the song in its native language."
                howItWorks="Paste stanzas and refrains line by line."
                mobileEffect="Rendered in easy-to-read typography on mobile screens during corporate and individual worship."
              />
            </Box>
            <TextField
              multiline
              rows={4}
              fullWidth
              margin="dense"
              value={song}
              required
              onChange={(e) => setSong(e.target.value)}
            />
          </Box>

          {language == '2' || language == '3' ?
            (
              <>
                <Box mt={2}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="subtitle2" fontWeight={600}>English Title (Optional)</Typography>
                    <HelpTooltip
                      title="English Transliteration / Title"
                      purpose="Provides an English transliteration or translation of the non-English song title."
                      howItWorks="Enter how the title sounds or translates in English."
                      mobileEffect="Enables English-speaking worshippers to discover and sing along."
                    />
                  </Box>
                  <TextField
                    size="small"
                    type="text"
                    fullWidth
                    value={engtitle}
                    onChange={(e) => setEngTitle(e.target.value)}
                  />
                </Box>

                <Box mt={2}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="subtitle2" fontWeight={600}>English Lyrics / Transliteration</Typography>
                    <HelpTooltip
                      title="English Lyrics"
                      purpose="Romanized or translated verses for believers learning the song."
                      howItWorks="Type or paste the English script representation of the lyrics."
                      mobileEffect="Displayed under a tab or toggle in the mobile app song viewer."
                    />
                  </Box>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    margin="dense"
                    value={engLyrics}
                    onChange={(e) => setEngLyrics(e.target.value)}
                  />
                </Box>
              </>
            ) : null}

          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            justifyContent="space-between"
            mt={2}
          >
            <Box flex="1 1 48%" mt={2}>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>Author / Songwriter</Typography>
                <HelpTooltip
                  title="Author Attribution"
                  purpose="Credits the hymn writer, composer, or ministry."
                  howItWorks="Enter the composer/author's name."
                  mobileEffect="Displayed as attribution metadata beneath the song title in the app."
                />
              </Box>
              <TextField
                size="small"
                type="text"
                value={author}
                fullWidth
                onChange={(e) => SetAuthor(e.target.value)}
              />
            </Box>

            <Box flex="1 1 48%" mt={2}>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>Copyright ? *</Typography>
                <HelpTooltip
                  title="Copyright Status"
                  purpose="Flags whether this hymn has active copyright restrictions or is public domain."
                  howItWorks="Select Yes if copyrighted by a publisher/CCLI, No if Public Domain."
                  mobileEffect="Controls copyright notice display and sharing restrictions in the mobile app."
                />
              </Box>
              <RadioGroup
                row
                value={copyright}
                onChange={(e) => setCopyright(e.target.value)}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  value="no"
                  control={<Radio />}
                  label="No"
                />
              </RadioGroup>
            </Box>

          </Box>




          {/* Pray Type */}





          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>

            <Button variant="contained" onClick={handleAddSong} sx={{ background: '#177373' }}>Save</Button>
          </Box>
        </Paper>
        <Toast toast={toast} setToast={setToast} />
      </Box>
    </>
  );
}
